import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiBanner } from 'omnium-ui/banner';
import { OuiDialogModule, OuiDialogService } from 'omnium-ui/dialog';
import { OuiTagModule } from 'omnium-ui/tag';
import { appRoutePaths } from '../../../../app.paths';
import {
  ActionAuthorizationService,
  doublonsBusterActionCodes
} from '../../../../core/auth/action-authorization.service';
import { AnchorNavigationComponent, AnchorNavigationItem } from '../../../../core/components/anchor-navigation/anchor-navigation.component';
import { ConsultantCardComponent } from '../../../../core/components/consultant-card/consultant-card.component';
import {
  InvestorGedDocuments,
  InvestorImmoOptionIntention,
  InvestorAtlasInspection,
  InvestorBoxPlacement,
  InvestorMinotaureInspection,
  MinotaureRecueilCrossConsultantPage,
  InvestorViewerCommand,
  InvestorViewerConsultantAssignment,
  InvestorViewerFinancialData,
  InvestorViewerOverview,
  InvestorViewerPerson,
  InvestorPurgeExecution,
} from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { countDistinctMetadataIds, formatAtlasGerePar, formatAtlasPersonnePhysiqueIds } from '../../shared/atlas-inspection.utils';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { SummaryGridComponent, SummaryGridItem } from '../../shared/summary-grid/summary-grid.component';
import { InvestorExternalReferencesSectionComponent } from './components/investor-external-references-section/investor-external-references-section.component';
import { InvestorGedSectionComponent } from './components/investor-ged-section/investor-ged-section.component';
import { InvestorPurgeCheckDialogComponent } from './components/investor-purge-check-dialog/investor-purge-check-dialog.component';
import { InvestorViewerAssetsSectionComponent } from './components/investor-viewer-assets-section/investor-viewer-assets-section.component';
import { InvestorViewerAccordionComponent } from './components/investor-viewer-accordion/investor-viewer-accordion.component';
import { InvestorViewerCommandsSectionComponent } from './components/investor-viewer-commands-section/investor-viewer-commands-section.component';
import { InvestorViewerConsultantsSectionComponent } from './components/investor-viewer-consultants-section/investor-viewer-consultants-section.component';
import { InvestorViewerLiabilitiesSectionComponent } from './components/investor-viewer-liabilities-section/investor-viewer-liabilities-section.component';
import { InvestorMinotaureSectionComponent } from './components/investor-minotaure-section/investor-minotaure-section.component';
import { InvestorViewerObjectivesSectionComponent } from './components/investor-viewer-objectives-section/investor-viewer-objectives-section.component';
import { InvestorViewerOverviewSectionComponent } from './components/investor-viewer-overview-section/investor-viewer-overview-section.component';
import { InvestorViewerPersonsSectionComponent } from './components/investor-viewer-persons-section/investor-viewer-persons-section.component';
import { InvestorViewerRevenueChargesSectionComponent } from './components/investor-viewer-revenue-charges-section/investor-viewer-revenue-charges-section.component';
import { InvestorViewerTaxSectionComponent } from './components/investor-viewer-tax-section/investor-viewer-tax-section.component';
import { InvestorStatusTagComponent } from '../../shared/investor-status-tag/investor-status-tag.component';

type InvestorDiagnostic = {
  key: string;
  label: string;
  status: 'completed' | 'error' | 'pending';
};

@Component({
  selector: 'dbw-investor-inspection-page',
  imports: [
    OuiBanner,
    OuiButtonModule,
    OuiDialogModule,
    OuiTagModule,
    AnchorNavigationComponent,
    ConsultantCardComponent,
    StateCardComponent,
    SummaryGridComponent,
    InvestorExternalReferencesSectionComponent,
    InvestorGedSectionComponent,
    InvestorMinotaureSectionComponent,
    InvestorViewerAccordionComponent,
    InvestorViewerAssetsSectionComponent,
    InvestorViewerCommandsSectionComponent,
    InvestorViewerOverviewSectionComponent,
    InvestorViewerConsultantsSectionComponent,
    InvestorViewerLiabilitiesSectionComponent,
    InvestorViewerObjectivesSectionComponent,
    InvestorViewerPersonsSectionComponent,
    InvestorViewerRevenueChargesSectionComponent,
    InvestorViewerTaxSectionComponent,
    InvestorStatusTagComponent,
  ],
  templateUrl: './investor-inspection.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./investor-viewer-section.shared.scss', './investor-inspection.component.scss'],
})
export class InvestorInspectionPageComponent implements OnInit {
  readonly destroyRef = inject(DestroyRef);

  readonly investorId = signal(0);
  readonly loading = signal(true);
  readonly error = signal<string | undefined>(undefined);
  readonly minotaureLoading = signal(false);
  readonly minotaureError = signal<string | null>(null);
  readonly minotaureRecueilCrossConsultantLoading = signal(false);
  readonly minotaureRecueilCrossConsultantError = signal<string | null>(null);
  readonly gedLoading = signal(false);
  readonly gedError = signal<string | null>(null);
  readonly boxPlacementLoading = signal(false);
  readonly boxPlacementError = signal<string | null>(null);
  readonly immoLoading = signal(false);
  readonly immoError = signal<string | null>(null);
  readonly viewerOverviewLoading = signal(false);
  readonly viewerOverviewError = signal<string | null>(null);
  readonly viewerPersonsLoading = signal(false);
  readonly viewerPersonsError = signal<string | null>(null);
  readonly viewerConsultantsLoading = signal(false);
  readonly viewerConsultantsError = signal<string | null>(null);
  readonly viewerFinancialLoading = signal(false);
  readonly viewerFinancialError = signal<string | null>(null);
  readonly viewerCommandsLoading = signal(false);
  readonly viewerCommandsError = signal<string | null>(null);
  readonly atlasInspection = signal<InvestorAtlasInspection | undefined>(undefined);
  readonly minotaureInspection = signal<InvestorMinotaureInspection | undefined>(undefined);
  readonly minotaureRecueilCrossConsultantPage = signal<MinotaureRecueilCrossConsultantPage | undefined>(undefined);
  readonly gedDocuments = signal<InvestorGedDocuments | null>(null);
  readonly boxPlacement = signal<InvestorBoxPlacement | null>(null);
  readonly immoOptionIntention = signal<InvestorImmoOptionIntention | null>(null);
  readonly viewerOverview = signal<InvestorViewerOverview | null>(null);
  readonly viewerPersons = signal<InvestorViewerPerson[] | null>(null);
  readonly viewerConsultants = signal<InvestorViewerConsultantAssignment[] | null>(null);
  readonly viewerFinancial = signal<InvestorViewerFinancialData | null>(null);
  readonly viewerCommands = signal<InvestorViewerCommand[] | null>(null);
  readonly activePurgeExecution = signal<InvestorPurgeExecution | null>(null);

  readonly purgeBannerExecution = computed(() => {
    const execution = this.activePurgeExecution();

    if (!execution) {
      return null;
    }

    return execution.isInProgress ? execution : null;
  });

  readonly correctionMetadataCount = computed(() => countDistinctMetadataIds(this.minotaureInspection()?.corrections ?? []));

  readonly anomalyMetadataCount = computed(() => countDistinctMetadataIds(this.minotaureInspection()?.anomalies ?? []));

  readonly minotaureRecueilCrossConsultantCount = computed(() => this.minotaureRecueilCrossConsultantPage()?.totalCount ?? 0);

  readonly globalAnomalyCount = computed(() =>
    this.diagnostics().filter(diagnostic => diagnostic.status === 'error').length
  );

  readonly relationCount = computed(() =>
    (this.viewerPersons() ?? []).reduce(
      (total, person) =>
        total + (person.coupleRelations?.length ?? 0) + (person.familyRelations?.length ?? 0) + (person.otherRelations?.length ?? 0),
      0
    )
  );

  readonly primaryConsultantRelationCount = computed(
    () =>
      (this.viewerConsultants() ?? []).filter(
        assignment => (assignment.investorRelationTypeCode ?? '').trim().toUpperCase() === 'PRIMAIRE' && assignment.isActive === true
      ).length
  );

  readonly gedDocumentCount = computed(() => this.gedDocuments()?.totalDocumentCount ?? 0);

  readonly viewerCommandCount = computed(() => this.viewerCommands()?.length ?? 0);

  readonly viewerContractCount = computed(() => (this.viewerCommands() ?? []).filter(command => !!command.contractNumber?.trim()).length);

  readonly viewerCommandOrigineCount = computed(() =>
    (this.viewerCommands() ?? []).filter(command => command.aramisSourceType === 'origine').length
  );

  readonly viewerCommandSecondaireCount = computed(() =>
    (this.viewerCommands() ?? []).filter(command => command.aramisSourceType === 'secondaire').length
  );

  readonly purgeBannerTitle = computed(() => {
    const execution = this.purgeBannerExecution();

    return 'Purge investisseur en cours';
  });

  readonly purgeBannerDeclination = computed<'alert'>(() => 'alert');
  readonly canExecutePurgeAction = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.investorPurgeExecute));

  readonly purgeBannerDescription = computed(() => {
    const execution = this.purgeBannerExecution();

    if (!execution) {
      return '';
    }

    const status = execution.status?.trim();
    const startedAt = execution.startedAtUtc ? new Date(execution.startedAtUtc).toLocaleString('fr-FR') : null;
    const parts = [`Demande de purge ${execution.executionId}`];

    if (status) {
      parts.push(`statut ${status}`);
    }

    if (startedAt) {
      parts.push(`demandée le ${startedAt}`);
    }

    return parts.join(' • ');
  });

  readonly consultantLabel = computed(() => {
    const investor = this.atlasInspection()?.investor;

    if (!investor) {
      return '-';
    }

    const consultantName = investor.consultantName?.trim();
    const consultantCode = investor.consultantCode?.trim();

    return consultantName || consultantCode || '-';
  });

  readonly gereParLabel = computed(() => formatAtlasGerePar(this.atlasInspection()?.investor.gerePar));

  readonly inspection = computed(() => {
    const atlas = this.atlasInspection();

    if (!atlas) {
      return undefined;
    }

    const minotaure = this.minotaureInspection();

    return {
      ...atlas,
      corrections: minotaure?.corrections ?? [],
      anomalies: minotaure?.anomalies ?? [],
      metadatas: minotaure?.metadatas ?? [],
    };
  });

  readonly summaryGridItems = computed<SummaryGridItem[]>(() => {
    const gedDocuments = this.gedDocuments();
    const boxPlacement = this.boxPlacement();
    const expectedPersonCount = this.viewerOverview()?.persons.length ?? 0;
    const loadedPersonCount = this.viewerPersons()?.length ?? 0;
    const relationsLoaded = expectedPersonCount === 0 || loadedPersonCount === expectedPersonCount || !!this.viewerPersonsError();

    return [
      {
        key: 'status',
        label: 'Statut investisseur',
        value: this.inspection()?.investor?.statusCode || '-',
        investorStatusCode: this.inspection()?.investor?.statusCode,
      },
      {
        key: 'gere-par',
        label: 'Géré par',
        value: this.gereParLabel(),
      },
      {
        key: 'consultant',
        label: 'Consultant',
        value: this.consultantLabel(),
      },
      {
        key: 'relations',
        label: 'Relations',
        value: relationsLoaded ? this.relationCount() : '...',
        meta: !relationsLoaded && expectedPersonCount > 0 ? `${loadedPersonCount} / ${expectedPersonCount} personne(s) chargée(s)` : '',
      },
      {
        key: 'commands-origine',
        label: 'Commandes fiche origine',
        value:
          this.viewerCommandsLoading() && this.viewerCommands() === null
            ? '...'
            : this.viewerCommandsError()
              ? 'Erreur'
              : this.viewerCommandOrigineCount(),
        meta:
          this.viewerCommandsError()
            ? 'Chargement en erreur'
            : this.viewerCommandsLoading() && this.viewerCommands() === null
              ? 'Chargement en cours'
              : '',
        aside:
          this.viewerCommandsLoading() && this.viewerCommands() === null
            ? { label: 'Chargement', status: 'pending' }
            : this.viewerCommandsError()
              ? { label: 'Erreur', status: 'error' }
              : undefined,
      },
      {
        key: 'commands-secondaire',
        label: 'Commandes fiche secondaire',
        value:
          this.viewerCommandsLoading() && this.viewerCommands() === null
            ? '...'
            : this.viewerCommandsError()
              ? 'Erreur'
              : this.viewerCommandSecondaireCount(),
        meta:
          this.viewerCommandsError()
            ? 'Chargement en erreur'
            : this.viewerCommandsLoading() && this.viewerCommands() === null
              ? 'Chargement en cours'
              : '',
        aside:
          this.viewerCommandsLoading() && this.viewerCommands() === null
            ? { label: 'Chargement', status: 'pending' }
            : this.viewerCommandsError()
              ? { label: 'Erreur', status: 'error' }
              : undefined,
      },
      {
        key: 'documents',
        label: 'Documents GED',
        value: this.gedLoading() && !gedDocuments ? '...' : this.gedDocumentCount(),
        meta: gedDocuments
          ? `${gedDocuments.activeDocumentCount} actif(s) / ${gedDocuments.inactiveDocumentCount} supprimé(s)`
          : this.gedError()
            ? 'Chargement en erreur'
            : this.gedLoading()
              ? 'Chargement en cours'
              : '',
      },
      {
        key: 'box-placement',
        label: 'Opérations Box',
        value:
          this.boxPlacementLoading() && !boxPlacement
            ? '...'
            : this.boxPlacementError()
              ? 'Erreur'
              : boxPlacement?.hasOperations
                ? 'Oui'
                : 'Non',
        meta: boxPlacement
          ? undefined
          : this.boxPlacementError()
            ? 'Chargement en erreur'
            : this.boxPlacementLoading()
              ? 'Chargement en cours'
              : '',
        aside:
          this.boxPlacementLoading() && !boxPlacement
            ? { label: 'Chargement', status: 'pending' }
            : this.boxPlacementError()
              ? { label: 'Erreur', status: 'error' }
              : undefined,
      },
      {
        key: 'anomalies',
        label: 'Anomalies',
        value: this.globalAnomalyCount(),
      },
    ];
  });

  readonly investorViewerAnchors = computed<AnchorNavigationItem[]>(() => {
    const anchors: AnchorNavigationItem[] = [
      {
        id: 'investor-viewer-fiche',
        label: 'Fiche investisseur',
      },
    ];

    const overview = this.viewerOverview();

    const assignments = this.viewerConsultants();
    if (assignments) {
      anchors.push({
        id: 'investor-viewer-consultants',
        label: 'Affectation consultant',
        itemsCount: assignments.length,
      });
    }

    if (overview) {
      anchors.push({
        id: 'investor-viewer-personnes-physiques',
        label: 'Pers. Physiques',
        itemsCount: overview.persons.length,
      });
    }

    const financialData = this.viewerFinancial();
    if (financialData) {
      anchors.push(
        {
          id: 'investor-viewer-objectifs',
          label: 'Objectifs',
          itemsCount: financialData.objectives.length,
        },
        {
          id: 'investor-viewer-actifs',
          label: 'Actifs',
          itemsCount: financialData.assets.length,
        },
        {
          id: 'investor-viewer-passifs',
          label: 'Passifs',
          itemsCount: financialData.liabilities.length,
        },
        {
          id: 'investor-viewer-revenus-charges',
          label: 'Revenus & charges',
          itemsCount: financialData.revenueAndCharges.length,
        },
        {
          id: 'investor-viewer-fiscalite',
          label: 'Fiscalité',
          itemsCount: financialData.taxes.length,
        }
      );
    }

    anchors.push(
      {
        id: 'investor-viewer-referentiels-externes',
        label: 'Référentiels externes',
        itemsCount: this.getExternalReferencesItemsCount(),
      },
      {
        id: 'investor-viewer-commandes-atlas',
        label: 'Commandes',
        itemsCount: this.viewerCommands()?.length,
      },
      {
        id: 'investor-viewer-documents-ged',
        label: 'Documents',
        itemsCount: this.gedDocuments()?.totalDocumentCount,
      },
      {
        id: 'investor-viewer-minotaure-metadatas',
        label: 'Minotaure',
        itemsCount: this.minotaureInspection()?.metadatas.length,
      }
    );

    return anchors;
  });

  readonly arianeIdDiagnostic = computed<InvestorDiagnostic | null>(() => {
    const atlas = this.atlasInspection();

    if (!atlas) {
      return null;
    }

    return {
      key: 'ariane-id',
      label: atlas.hasDuplicatedArianeId ? 'ArianeId en doublon' : 'ArianeId unique',
      status: atlas.hasDuplicatedArianeId ? 'error' : 'completed',
    };
  });

  readonly personnePhysiqueDiagnostic = computed<InvestorDiagnostic | null>(() => {
    const atlas = this.atlasInspection();

    if (!atlas) {
      return null;
    }

    const personCount = atlas.investor.personnePhysiqueIds.length;

    return {
      key: 'personne-physique',
      label:
        personCount === 1
          ? '1 seule Personne Physique'
          : personCount === 0
            ? 'Aucune Personne Physique'
            : `${personCount} Personnes Physiques`,
      status: personCount === 1 ? 'completed' : 'error',
    };
  });

  readonly consultantPrimaireDiagnostic = computed<InvestorDiagnostic | null>(() => {
    if (!this.atlasInspection()) {
      return null;
    }

    const primaryConsultantRelationCount = this.primaryConsultantRelationCount();
    const consultantRelationsLoaded = this.viewerConsultants() !== null || this.viewerConsultantsError();

    return {
      key: 'consultant-primaire',
      label: !consultantRelationsLoaded
        ? 'Relations consultant en cours de chargement'
        : primaryConsultantRelationCount === 1
          ? '1 relation primaire consultant'
          : `${primaryConsultantRelationCount} relation primaire consultant`,
      status: !consultantRelationsLoaded ? 'pending' : primaryConsultantRelationCount === 1 ? 'completed' : 'error',
    };
  });

  readonly minotaureAnomaliesDiagnostic = computed<InvestorDiagnostic | null>(() => {
    if (!this.atlasInspection()) {
      return null;
    }

    const anomalyCount = this.anomalyMetadataCount();

    return {
      key: 'minotaure-anomalies',
      label: this.minotaureLoading()
        ? 'Diagnostic Minotaure en cours'
        : anomalyCount === 0
          ? "Pas d'anomalie dans les metadata Minotaure"
          : `${anomalyCount} anomalie(s) dans les metadata Minotaure`,
      status: this.minotaureLoading() ? 'pending' : anomalyCount === 0 ? 'completed' : 'error',
    };
  });

  readonly minotaureRecueilCrossConsultantDiagnostic = computed<InvestorDiagnostic | null>(() => {
    if (!this.atlasInspection()) {
      return null;
    }

    const recueilCount = this.minotaureRecueilCrossConsultantCount();

    return {
      key: 'minotaure-recueil-cross-consultant',
      label: this.minotaureRecueilCrossConsultantLoading()
        ? 'Diagnostic recueil Minotaure en cours'
        : this.minotaureRecueilCrossConsultantError()
          ? 'Diagnostic recueil Minotaure indisponible'
          : recueilCount === 0
            ? 'Aucun recueil partagé entre consultants'
            : `${recueilCount} élément(s) recueil partagé(s) entre consultants`,
      status: this.minotaureRecueilCrossConsultantLoading()
        ? 'pending'
        : this.minotaureRecueilCrossConsultantError() || recueilCount > 0
          ? 'error'
          : 'completed',
    };
  });

  readonly crossConsultantRelationsDiagnostic = computed<InvestorDiagnostic | null>(() => {
    const atlas = this.atlasInspection();

    if (!atlas) {
      return null;
    }

    return {
      key: 'cross-consultant-relations',
      label: atlas.hasCrossConsultantRelations
        ? `${atlas.crossConsultantRelationCount} relation(s) vers ${atlas.crossConsultantRelatedInvestorCount} investisseur(s) d'un autre consultant`
        : 'Aucune relation vers un autre consultant',
      status: atlas.hasCrossConsultantRelations ? 'error' : 'completed',
    };
  });

  readonly diagnostics = computed<InvestorDiagnostic[]>(() => {
    const diagnostics = [
      this.arianeIdDiagnostic(),
      this.personnePhysiqueDiagnostic(),
      this.consultantPrimaireDiagnostic(),
      this.minotaureAnomaliesDiagnostic(),
      this.minotaureRecueilCrossConsultantDiagnostic(),
      this.crossConsultantRelationsDiagnostic(),
    ];

    return diagnostics.filter((diagnostic): diagnostic is InvestorDiagnostic => diagnostic !== null);
  });

  readonly summaryMessage = computed(() => {
    const atlas = this.atlasInspection();

    if (!atlas) {
      return '';
    }

    const parts: string[] = [];

    if (atlas.duplicatedArianeGroup) {
      parts.push(
        `ArianeId ${atlas.duplicatedArianeGroup.arianeId} partagé par ${atlas.duplicatedArianeGroup.duplicateCount} investisseurs`
      );
    }

    if (!atlas.hasPersonnePhysique) {
      parts.push('aucune personne physique Atlas liée');
    } else if (atlas.hasMultiplePersonnesPhysiques) {
      parts.push(`${atlas.investor.personnePhysiqueIds.length} personnes physiques Atlas liées`);
    }

    const correctionMetadataCount = this.correctionMetadataCount();
    if (correctionMetadataCount > 0) {
      parts.push(`${correctionMetadataCount} metadata(s) candidate(s) à correction`);
    }

    const anomalyMetadataCount = this.anomalyMetadataCount();
    if (anomalyMetadataCount > 0) {
      parts.push(`${anomalyMetadataCount} metadata(s) en anomalie`);
    }

    if (this.minotaureLoading()) {
      parts.push('chargement du contexte Minotaure');
    }

    const recueilCrossConsultantCount = this.minotaureRecueilCrossConsultantCount();
    if (recueilCrossConsultantCount > 0) {
      parts.push(`${recueilCrossConsultantCount} élément(s) recueil partagé(s) entre consultants`);
    }

    return parts.length === 0 ? "Aucun problème connu n'est actuellement remonté pour cet investisseur." : parts.join(' • ');
  });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly apiService: DoublonsBusterApiService,
    private readonly actionAuthorizationService: ActionAuthorizationService,
    private readonly titleService: Title,
    private readonly dialogService: OuiDialogService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(paramMap => {
      const investorId = Number(paramMap.get('investorId'));

      if (!Number.isFinite(investorId) || investorId <= 0) {
        this.error.set('Investisseur invalide.');
        this.titleService.setTitle('Investisseur invalide');
        this.loading.set(false);
        return;
      }

      this.investorId.set(investorId);
      this.updateDocumentTitle();
      this.loadInspection();
    });
  }

  reload(): void {
    this.loadInspection();
  }

  reloadViewerPersons(): void {
    const summaries = this.viewerOverview()?.persons;

    if (!summaries) {
      return;
    }

    this.loadViewerPersonDetails(summaries);
  }

  openViewerSearch(): void {
    this.router.navigateByUrl(appRoutePaths.atlas.rechercheInvestisseur);
  }

  openDuplicatedArianePage(): void {
    this.router.navigateByUrl(appRoutePaths.atlas.doublonsAriane);
  }

  openDuplicatedPersonnePhysiquePage(): void {
    this.router.navigateByUrl(appRoutePaths.atlas.doublonsPersonnePhysique);
  }

  openCrossConsultantRelationsPage(): void {
    this.router.navigateByUrl(appRoutePaths.atlas.relationsInterConsultants);
  }

  openPurgeDialog(): void {
    if (!this.canExecutePurgeAction()) {
      return;
    }

    this.dialogService.openDialog(
      InvestorPurgeCheckDialogComponent,
      {
        investorId: this.investorId(),
        investorLabel: this.inspection()?.investor?.displayName || `Investisseur #${this.investorId()}`,
      },
      undefined,
      '820px'
    ).afterClosed().subscribe(result => {
      if (result?.purgeStarted === true) {
        this.loadActivePurgeExecution();
      }
    });
  }

  openInvestor(investorId: number): void {
    if (investorId === this.investorId()) {
      return;
    }

    this.router.navigateByUrl(appRoutePaths.atlas.investor(investorId));
  }

  openArianeDuplicate(arianeId: number): void {
    this.router.navigateByUrl(appRoutePaths.atlas.arianeDuplicate(arianeId));
  }

  formatPersonnePhysiqueIds(ids: number[]): string {
    return formatAtlasPersonnePhysiqueIds(ids);
  }

  private getExternalReferencesItemsCount(): number | undefined {
    const boxPlacement = this.boxPlacement();
    const immoOptionIntention = this.immoOptionIntention();

    if (!boxPlacement && !immoOptionIntention) {
      return undefined;
    }

    let count = 0;

    if (boxPlacement?.hasOperations) {
      count += 1;
    }

    if (immoOptionIntention?.hasOptionIntention) {
      count += 1;
    }

    return count;
  }

  private loadInspection(): void {
    this.loading.set(true);
    this.error.set(undefined);
    this.minotaureLoading.set(true);
    this.minotaureError.set(null);
    this.minotaureRecueilCrossConsultantLoading.set(true);
    this.minotaureRecueilCrossConsultantError.set(null);
    this.gedLoading.set(true);
    this.gedError.set(null);
    this.boxPlacementLoading.set(true);
    this.boxPlacementError.set(null);
    this.immoLoading.set(true);
    this.immoError.set(null);
    this.viewerOverviewLoading.set(false);
    this.viewerOverviewError.set(null);
    this.viewerPersonsLoading.set(false);
    this.viewerPersonsError.set(null);
    this.viewerConsultantsLoading.set(false);
    this.viewerConsultantsError.set(null);
    this.viewerFinancialLoading.set(false);
    this.viewerFinancialError.set(null);
    this.viewerCommandsLoading.set(false);
    this.viewerCommandsError.set(null);
    this.atlasInspection.set(undefined);
    this.minotaureInspection.set(undefined);
    this.minotaureRecueilCrossConsultantPage.set(undefined);
    this.gedDocuments.set(null);
    this.boxPlacement.set(null);
    this.immoOptionIntention.set(null);
    this.viewerOverview.set(null);
    this.viewerPersons.set(null);
    this.viewerConsultants.set(null);
    this.viewerFinancial.set(null);
    this.viewerCommands.set(null);
    this.activePurgeExecution.set(null);
    this.loadActivePurgeExecution();

    this.apiService.getInvestorAtlasInspection(this.investorId()).subscribe({
      next: inspection => {
        this.atlasInspection.set(inspection);
        this.updateDocumentTitle(inspection.investor.displayName);
        this.loading.set(false);
        this.loadViewerSections();
      },
      error: error => {
        this.atlasInspection.set(undefined);
        this.minotaureInspection.set(undefined);
        this.viewerOverview.set(null);
        this.viewerPersons.set(null);
        this.viewerConsultants.set(null);
        this.viewerFinancial.set(null);
        this.updateDocumentTitle();
        this.error.set(
          error?.status === 404
            ? "L'investisseur demandé est introuvable."
            : (error?.error?.detail ?? "Impossible de charger l'inspection investisseur.")
        );
        this.loading.set(false);
      },
    });

    this.apiService.getInvestorMinotaureInspection(this.investorId()).subscribe({
      next: inspection => {
        this.minotaureInspection.set(inspection);
        this.minotaureLoading.set(false);
      },
      error: error => {
        this.minotaureInspection.set(undefined);
        this.minotaureError.set(
          error?.status === 404
            ? 'Le contexte Minotaure est indisponible pour cet investisseur.'
            : (error?.error?.detail ?? 'Impossible de charger les données Minotaure.')
        );
        this.minotaureLoading.set(false);
      },
    });

    this.apiService.getMinotaureRecueilCrossConsultantPage(0, 1, { investorId: this.investorId() }).subscribe({
      next: page => {
        this.minotaureRecueilCrossConsultantPage.set(page);
        this.minotaureRecueilCrossConsultantLoading.set(false);
      },
      error: error => {
        this.minotaureRecueilCrossConsultantPage.set(undefined);
        this.minotaureRecueilCrossConsultantError.set(
          error?.status === 404
            ? 'Le reporting recueil Minotaure est indisponible pour cet investisseur.'
            : (error?.error?.detail ?? 'Impossible de charger le diagnostic recueil Minotaure.')
        );
        this.minotaureRecueilCrossConsultantLoading.set(false);
      },
    });

    this.apiService.getInvestorGedDocuments(this.investorId()).subscribe({
      next: documents => {
        this.gedDocuments.set(documents);
        this.gedLoading.set(false);
      },
      error: error => {
        this.gedDocuments.set(null);
        this.gedError.set(
          error?.status === 404
            ? 'Le contexte GED est indisponible pour cet investisseur.'
            : (error?.error?.detail ?? 'Impossible de charger les documents GED.')
        );
        this.gedLoading.set(false);
      },
    });

    this.apiService.getInvestorBoxPlacement(this.investorId()).subscribe({
      next: boxPlacement => {
        this.boxPlacement.set(boxPlacement);
        this.boxPlacementLoading.set(false);
      },
      error: error => {
        this.boxPlacement.set(null);
        this.boxPlacementError.set(error?.error?.detail ?? 'Impossible de charger les informations Box Placement.');
        this.boxPlacementLoading.set(false);
      },
    });

    this.apiService.getInvestorImmoOptionIntention(this.investorId()).subscribe({
      next: immoOptionIntention => {
        this.immoOptionIntention.set(immoOptionIntention);
        this.immoLoading.set(false);
      },
      error: error => {
        this.immoOptionIntention.set(null);
        this.immoError.set(error?.error?.detail ?? 'Impossible de charger les informations Immo.');
        this.immoLoading.set(false);
      },
    });
  }

  private loadActivePurgeExecution(): void {
    const investorId = this.investorId();

    if (!Number.isFinite(investorId) || investorId <= 0) {
      this.activePurgeExecution.set(null);
      return;
    }

    this.apiService.getActiveInvestorPurgeExecution(investorId).subscribe({
      next: execution => {
        this.activePurgeExecution.set(execution);
      },
      error: error => {
        if (error?.status === 404) {
          this.activePurgeExecution.set(null);
        }
      },
    });
  }

  private loadViewerSections(): void {
    this.viewerOverviewLoading.set(true);
    this.viewerOverviewError.set(null);
    this.viewerPersonsLoading.set(true);
    this.viewerPersonsError.set(null);
    this.viewerConsultantsLoading.set(true);
    this.viewerConsultantsError.set(null);
    this.viewerFinancialLoading.set(true);
    this.viewerFinancialError.set(null);
    this.viewerCommandsLoading.set(true);
    this.viewerCommandsError.set(null);

    this.apiService.getInvestorAtlasViewerOverview(this.investorId()).subscribe({
      next: overview => {
        this.viewerOverview.set(overview);
        this.viewerOverviewLoading.set(false);
        this.loadViewerPersonDetails(overview.persons);
      },
      error: error => {
        this.viewerOverview.set(null);
        this.viewerPersons.set(null);
        this.viewerOverviewError.set(
          error?.status === 404
            ? 'Le viewer Atlas est indisponible pour cet investisseur.'
            : (error?.error?.detail ?? 'Impossible de charger la synthèse du viewer Atlas.')
        );
        this.viewerOverviewLoading.set(false);
        this.viewerPersonsLoading.set(false);
      },
    });

    this.apiService.getInvestorAtlasViewerConsultants(this.investorId()).subscribe({
      next: assignments => {
        this.viewerConsultants.set(assignments);
        this.viewerConsultantsLoading.set(false);
      },
      error: error => {
        this.viewerConsultants.set(null);
        this.viewerConsultantsError.set(error?.error?.detail ?? 'Impossible de charger les affectations consultants Atlas.');
        this.viewerConsultantsLoading.set(false);
      },
    });

    this.apiService.getInvestorAtlasViewerFinancialData(this.investorId()).subscribe({
      next: financialData => {
        this.viewerFinancial.set(financialData);
        this.viewerFinancialLoading.set(false);
      },
      error: error => {
        this.viewerFinancial.set(null);
        this.viewerFinancialError.set(error?.error?.detail ?? 'Impossible de charger les données financières du viewer Atlas.');
        this.viewerFinancialLoading.set(false);
      },
    });

    this.apiService.getInvestorAtlasViewerCommands(this.investorId()).subscribe({
      next: commands => {
        this.viewerCommands.set(commands);
        this.viewerCommandsLoading.set(false);
      },
      error: error => {
        this.viewerCommands.set(null);
        this.viewerCommandsError.set(error?.error?.detail ?? 'Impossible de charger les Commandes.');
        this.viewerCommandsLoading.set(false);
      },
    });
  }

  private loadViewerPersonDetails(summaries: InvestorViewerOverview['persons']): void {
    if (summaries.length === 0) {
      this.viewerPersons.set([]);
      this.viewerPersonsLoading.set(false);
      this.viewerPersonsError.set(null);
      return;
    }

    const personOrder = new Map(summaries.map((summary, index) => [summary.personId, index]));
    let pendingRequests = summaries.length;
    let failedRequests = 0;

    this.viewerPersons.set([]);
    this.viewerPersonsLoading.set(true);
    this.viewerPersonsError.set(null);

    for (const summary of summaries) {
      this.apiService.getInvestorAtlasViewerPerson(this.investorId(), summary.personId).subscribe({
        next: person => {
          const currentPersons = this.viewerPersons() ?? [];
          const nextPersons = [...currentPersons, person]
            .filter((candidate, index, source) => source.findIndex(existing => existing.personId === candidate.personId) === index)
            .sort(
              (left, right) =>
                (personOrder.get(left.personId) ?? Number.MAX_SAFE_INTEGER) - (personOrder.get(right.personId) ?? Number.MAX_SAFE_INTEGER)
            );

          this.viewerPersons.set(nextPersons);
          pendingRequests -= 1;
          this.completeViewerPersonsLoading(pendingRequests, failedRequests);
        },
        error: () => {
          failedRequests += 1;
          pendingRequests -= 1;
          this.completeViewerPersonsLoading(pendingRequests, failedRequests);
        },
      });
    }
  }

  private completeViewerPersonsLoading(pendingRequests: number, failedRequests: number): void {
    if (pendingRequests > 0) {
      return;
    }

    if (failedRequests > 0) {
      this.viewerPersonsError.set(
        failedRequests === 1
          ? "Une personne physique n'a pas pu être chargée."
          : `${failedRequests} personnes physiques n'ont pas pu être chargées.`
      );
    }

    this.viewerPersonsLoading.set(false);
  }

  private updateDocumentTitle(displayName?: string | null): void {
    const title = displayName?.trim() || `Investisseur #${this.investorId()}`;
    this.titleService.setTitle(title);
  }
}
